import { useState, useEffect } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import Select from "react-select";
import Swal from "sweetalert2";

// Mismo esquema de 11 biológicos que ya usaba el paso de MRCV dentro
// del wizard — se mantiene igual acá.
const MRCV_VACUNAS = [
  { clave: "bcg", nombre: "BCG", grupo: "Recién nacido" },
  { clave: "rotavirus2", nombre: "Rotavirus 2ª dosis", grupo: "< 6 Meses" },
  { clave: "neumococo2", nombre: "Neumococo 2ª dosis", grupo: "< 6 Meses" },
  { clave: "polio3", nombre: "Polio 3", grupo: "< 12 Meses" },
  { clave: "pentavalente3", nombre: "Pentavalente 3", grupo: "< 12 Meses" },
  { clave: "neumococo_refuerzo", nombre: "Neumococo refuerzo", grupo: "12-23 Meses" },
  { clave: "triple_viral_1", nombre: "Triple viral 1ª dosis", grupo: "12-23 Meses" },
  { clave: "fiebre_amarilla", nombre: "Fiebre Amarilla", grupo: "12-23 Meses" },
  { clave: "triple_viral_refuerzo18", nombre: "Triple viral refuerzo 18 meses", grupo: "18-23 Meses" },
  { clave: "hepatitis_a", nombre: "Hepatitis A", grupo: "12-23 Meses" },
  { clave: "triple_viral_2a5", nombre: "Triple viral refuerzo 2-5 años", grupo: "24-60 Meses" },
];

// Los 18 códigos oficiales — pregunta 40, "Motivos de no vacunación",
// agrupados en las 3 categorías del instrumento.
const MOTIVOS_NO_VACUNACION = [
  { grupo: "Relacionados con la logística y las estrategias del programa", opciones: [
    "Lugar de vacunación lejano o cerrado, ausencia del vacunador",
    "Horario de vacunación restringido",
    "Largos tiempos de espera",
    "No había vacunas o insumos",
    "Falta o pérdida de carné de vacunación",
    "No afiliado",
    "Desconocimiento de la jornada",
    "Desconocimiento que las vacunas son gratis",
  ]},
  { grupo: "Relacionados con la salud del usuario", opciones: [
    "Niño enfermo",
    "Niño hospitalizado",
    "Orden médica por enfermedad",
    "Reacción adversa a dosis previas (leve, moderada o grave)",
    "El médico dijo que tenía las vacunas completas",
    "Peso menor a 2000",
    "Anafilaxia a dosis previas (reacción alérgica grave que compromete la vida del niño)",
  ]},
  { grupo: "Relacionados con el cuidador o familia", opciones: [
    "Falta de tiempo del cuidador",
    "Rechazo de la vacunación",
    "Porque tiene las vacunas completas",
  ]},
];

const VACIO = {
  nombre_madre: "", documento_madre: "", telefono: "",
  regimen: "", aseguradora: "", etnia: "",
  condicion_desplazamiento: "NO",
  fecha_encuesta: new Date().toISOString().split("T")[0],
  fecha_nacimiento: "",
  esquema_completo: "",
  motivo_no_vacunacion: "",
};

const selectStyles = {
  control: (base) => ({ ...base, minHeight: 31, fontSize: 13.5 }),
  menuPortal: (base) => ({ ...base, zIndex: 3000 }),
};
const opcionesSelect = (lista) => (lista || []).map(v => ({ value: v, label: v }));
const valorSelect = (v) => (v ? { value: v, label: v } : null);

/**
 * Modal exclusivo — mismo patrón que MrcvSinCarnetModal, pero para
 * quien SÍ presenta carnet o validación PAIWEB: pide los mismos datos
 * de madre/acudiente y afiliación, y además el esquema completo
 * (fecha de aplicación + verificación PAIWEB por cada biológico).
 *
 * `menor` trae lo ya llenado en PAI (nombre, edad, numero_identificacion,
 * fotoA_base64/fotoB_base64 si las hay) — no se vuelve a pedir.
 */
export default function MrcvConCarnetModal({ show, onClose, menor, catalogos, onGuardar }) {
  const [form, setForm] = useState(VACIO);
  const [vacunas, setVacunas] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (show) { setForm(VACIO); setVacunas({}); setErrors({}); }
  }, [show, menor?.numero_identificacion]);

  const set = (campo, valor) => setForm(p => ({ ...p, [campo]: valor }));
  const opcionesDe = (tipo) => catalogos?.[tipo] || [];

  const guardar = () => {
    const e = {};
    if (!form.fecha_encuesta) e.fecha_encuesta = true;
    if (!form.fecha_nacimiento) e.fecha_nacimiento = true;
    if (!form.nombre_madre.trim()) e.nombre_madre = true;
    if (!form.documento_madre.trim()) e.documento_madre = true;
    if (!form.regimen) e.regimen = true;
    if (!form.aseguradora) e.aseguradora = true;
    if (!form.esquema_completo) e.esquema_completo = true;
    if (form.esquema_completo === "No" && !form.motivo_no_vacunacion) e.motivo_no_vacunacion = true;
    setErrors(e);
    if (Object.keys(e).length > 0) {
      Swal.fire({ icon: "warning", title: "Faltan datos por completar", timer: 2200, showConfirmButton: false });
      return;
    }

    const entrada = {
      nombre_nino: menor.nombre,
      documento_nino: menor.numero_identificacion,
      tiene_carnet: "SI",
      ...form,
    };
    MRCV_VACUNAS.forEach(({ clave }) => {
      entrada[`${clave}_fecha`] = vacunas[clave]?.fecha || null;
      entrada[`${clave}_verificado`] = vacunas[clave]?.verificado || null;
    });
    onGuardar(entrada);
  };

  if (!menor) return null;

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static" size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>📋 MRCV — {menor.nombre} ({menor.edad} {menor.unidad_edad})</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {(menor.fotoA_base64 || menor.fotoB_base64) && (
          <div className="d-flex gap-2 mb-3 align-items-center">
            {menor.fotoA_base64 && <img src={menor.fotoA_base64} alt="Carné cara A" style={{ width: 70, height: 70, objectFit: "cover", borderRadius: 8, border: "1px solid #dee2e6" }} />}
            {menor.fotoB_base64 && <img src={menor.fotoB_base64} alt="Carné cara B" style={{ width: 70, height: 70, objectFit: "cover", borderRadius: 8, border: "1px solid #dee2e6" }} />}
            <div className="text-muted" style={{ fontSize: 10.5 }}>📷 Carné del barrido — no hace falta volver a tomarla</div>
          </div>
        )}

        <div className="row g-2 mb-2">
          <div className="col-6">
            <Form.Label style={{ fontSize: 11 }}>Fecha de nacimiento *</Form.Label>
            <Form.Control size="sm" type="date" max={new Date().toISOString().split("T")[0]} isInvalid={!!errors.fecha_nacimiento} value={form.fecha_nacimiento} onChange={e => set("fecha_nacimiento", e.target.value)} />
          </div>
          <div className="col-6">
            <Form.Label style={{ fontSize: 11 }}>Fecha de aplicación *</Form.Label>
            <Form.Control size="sm" type="date" isInvalid={!!errors.fecha_encuesta} value={form.fecha_encuesta} onChange={e => set("fecha_encuesta", e.target.value)} />
          </div>
        </div>

        <p className="fw-bold mt-2 mb-2" style={{ fontSize: 12.5 }}>Madre / acudiente</p>
        <div className="row g-2 mb-2">
          <div className="col-12 col-md-6">
            <Form.Label style={{ fontSize: 11 }}>Nombre completo *</Form.Label>
            <Form.Control size="sm" isInvalid={!!errors.nombre_madre} value={form.nombre_madre} onChange={e => set("nombre_madre", e.target.value)} />
          </div>
          <div className="col-6 col-md-3">
            <Form.Label style={{ fontSize: 11 }}>N° de identificación *</Form.Label>
            <Form.Control size="sm" isInvalid={!!errors.documento_madre} value={form.documento_madre} onChange={e => set("documento_madre", e.target.value)} />
          </div>
          <div className="col-6 col-md-3">
            <Form.Label style={{ fontSize: 11 }}>Teléfono</Form.Label>
            <Form.Control size="sm" value={form.telefono} onChange={e => set("telefono", e.target.value)} />
          </div>
        </div>

        <p className="fw-bold mt-3 mb-2" style={{ fontSize: 12.5 }}>Afiliación y enfoque</p>
        <div className="row g-2 mb-2">
          <div className="col-6 col-md-3">
            <Form.Label style={{ fontSize: 11 }}>Régimen *</Form.Label>
            <Form.Select size="sm" isInvalid={!!errors.regimen} value={form.regimen} onChange={e => set("regimen", e.target.value)}>
              <option value="">Selecciona...</option>
              {opcionesDe("regimenes-afiliacion").map(v => <option key={v} value={v}>{v}</option>)}
            </Form.Select>
          </div>
          <div className="col-6 col-md-3">
            <Form.Label style={{ fontSize: 11 }}>EPS *</Form.Label>
            <Select placeholder="Buscar EPS..." options={opcionesSelect(opcionesDe("eps"))} value={valorSelect(form.aseguradora)}
              onChange={sel => set("aseguradora", sel?.value || "")} isClearable menuPlacement="auto" menuPosition="fixed"
              styles={{ ...selectStyles, control: (b) => ({ ...selectStyles.control(b), borderColor: errors.aseguradora ? "#dc3545" : b.borderColor }) }}
              menuPortalTarget={document.body} />
          </div>
          <div className="col-6 col-md-3">
            <Form.Label style={{ fontSize: 11 }}>Etnia</Form.Label>
            <Select placeholder="Buscar etnia..." options={opcionesSelect(opcionesDe("etnias"))} value={valorSelect(form.etnia)}
              onChange={sel => set("etnia", sel?.value || "")} isClearable menuPlacement="auto" menuPosition="fixed"
              styles={selectStyles} menuPortalTarget={document.body} />
          </div>
          <div className="col-6 col-md-3">
            <Form.Label style={{ fontSize: 11 }}>¿Desplazamiento?</Form.Label>
            <div className="d-flex gap-1">
              {["SI", "NO"].map(v => (
                <Button key={v} type="button" size="sm" className="flex-grow-1"
                  variant={form.condicion_desplazamiento === v ? (v === "SI" ? "warning" : "outline-secondary") : "outline-secondary"}
                  onClick={() => set("condicion_desplazamiento", v)}>{v}</Button>
              ))}
            </div>
          </div>
        </div>

        <Form.Label style={{ fontSize: 11 }}>¿Esquema completo? *</Form.Label>
        <div className="d-flex gap-2 mb-2">
          {["Si", "No"].map(v => (
            <Button key={v} type="button" size="sm" className="flex-grow-1"
              variant={form.esquema_completo === v ? (v === "Si" ? "success" : "warning") : (errors.esquema_completo ? "outline-danger" : "outline-secondary")}
              onClick={() => set("esquema_completo", v)}>
              {v}
            </Button>
          ))}
        </div>

        {form.esquema_completo === "No" && (
          <>
            <Form.Label style={{ fontSize: 11 }}>Motivo de no vacunación *</Form.Label>
            <Form.Select size="sm" className="mb-2" isInvalid={!!errors.motivo_no_vacunacion} value={form.motivo_no_vacunacion} onChange={e => set("motivo_no_vacunacion", e.target.value)}>
              <option value="">Selecciona el motivo...</option>
              {MOTIVOS_NO_VACUNACION.map(({ grupo, opciones }) => (
                <optgroup key={grupo} label={grupo}>
                  {opciones.map(m => <option key={m} value={m}>{m}</option>)}
                </optgroup>
              ))}
            </Form.Select>
          </>
        )}

        <p className="fw-bold mt-3 mb-2" style={{ fontSize: 12.5 }}>💉 Esquema — fecha de aplicación y verificación PAIWEB</p>
        {MRCV_VACUNAS.map(({ clave, nombre, grupo }) => (
          <div key={clave} className="row g-2 align-items-end mb-2 pb-2" style={{ borderBottom: "1px solid #f1f3f5" }}>
            <div className="col-12 col-md-5">
              <div style={{ fontSize: 12, fontWeight: 700 }}>{nombre}</div>
              <div style={{ fontSize: 10, color: "#8a94a6" }}>{grupo}</div>
            </div>
            <div className="col-7 col-md-4">
              <Form.Label style={{ fontSize: 10 }}>Fecha de aplicación</Form.Label>
              <Form.Control size="sm" type="date" max={new Date().toISOString().split("T")[0]}
                value={vacunas[clave]?.fecha || ""} onChange={e => setVacunas(p => ({ ...p, [clave]: { ...p[clave], fecha: e.target.value } }))} />
            </div>
            <div className="col-5 col-md-3">
              <Form.Label style={{ fontSize: 10 }}>Verificado PAIWEB</Form.Label>
              <Form.Select size="sm" value={vacunas[clave]?.verificado || ""} onChange={e => setVacunas(p => ({ ...p, [clave]: { ...p[clave], verificado: e.target.value } }))}>
                <option value="">—</option>
                <option value="SI">SI</option>
                <option value="NO">NO</option>
              </Form.Select>
            </div>
          </div>
        ))}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" size="sm" onClick={onClose}>Cancelar</Button>
        <Button variant="success" size="sm" onClick={guardar}>✓ Guardar MRCV</Button>
      </Modal.Footer>
    </Modal>
  );
}